import {
  AccountableType,
  PaymentSource,
  MessageLogType,
  PaymentType,
  PracticeManagement,
  OfficeStatus,
  MembershipType,
  PaymentCycle,
  TransactionStatus,
  MessageSource,
  ClaimStatus,
  ClaimSubStatus,
  StatementTemplate,
  StatementSource,
  StatementStatus,
  MessageLogStatus,
  IntakeFormStatus,
} from "@prisma/client";

import { formatPMS } from "@calcom/lib/format";

export const TERMINAL_DEVICE_PRICE = 600;
export const DENTIVE_TERMINAL_DEVICE_PRICE = 400;

type OutreachOptionsType = {
  value?: MessageLogType;
  label: string;
};

type StringOptionsType = {
  value: string;
  label: string;
};

type MembershipOptionType = {
  value: MembershipType;
  label: string;
};

export enum MembershipPendingReason {
  StartDate = "Start Date",
  PatientApproval = "Patient Approval",
}

export enum AutoCollectionRule {
  Family = "Family",
  Individual = "Individual",
}

export const OutreachOptions: Array<OutreachOptionsType> = [
  { value: undefined, label: "Communication Type" },
  { value: MessageLogType.SMS, label: "SMS" },
  { value: MessageLogType.EMAIL, label: "EMAIL" },
];

export const OutreachStatusOptions: Array<{ value?: MessageLogStatus; label: string }> = [
  { value: undefined, label: "Message Status" },
  { value: MessageLogStatus.DELIVERED, label: "Delivered" },
  { value: MessageLogStatus.FAILED, label: "Failed" },
];

type ChargeOptionsType = {
  value?: PaymentType;
  label: string;
};

export const ChargeOptions: Array<ChargeOptionsType> = [
  { value: undefined, label: "All Types" },
  { value: PaymentType.PROMPTED, label: "Prompted" },
  { value: PaymentType.PROCESSED, label: "Processed" },
];

export const PaymentPlanOptions: Array<{
  value?: string;
  label: string;
}> = [
  { value: "active", label: "All Plans" },
  { value: "created-date", label: "Created Date" },
];

export const TransactionStatusOptions: Array<{ value?: TransactionStatus; label: string }> = [
  { value: undefined, label: "Transaction State" },
  { value: TransactionStatus.SUCCESSFUL, label: "Success" },
  { value: TransactionStatus.REFUND, label: "Refund" },
  { value: TransactionStatus.FAILED, label: "Failed" },
  { value: TransactionStatus.DISPUTE, label: "Dispute" },
];

export const MessageSourceOptions: Array<{ value: MessageSource; label: string }> = [
  { value: MessageSource.AUTOMATEDAR, label: "Auto Collection" },
  { value: MessageSource.MEMBERSHIP, label: "Subscription" },
  { value: MessageSource.PAYMENTPLAN, label: "Payment Plan" },
  { value: MessageSource.PAYMENT_REQUEST, label: "Payment Request" },
  { value: MessageSource.CARD_REQUEST, label: "Card Request" },
  { value: MessageSource.CONFIRMATION, label: "Confirmation" },
  { value: MessageSource.INTAKEFORM_REQUEST, label: "Intake Form" },
  { value: MessageSource.APPOINTMENT_REMINDER, label: "Appointment Reminder" },
];

export const PaymentSourceOptions: Array<{ value: PaymentSource; label: string }> = [
  // { value: undefined, label: "All Transaction Source" },
  { value: PaymentSource.AUTOMATEDAR, label: "Auto Collection" },
  { value: PaymentSource.PAYMENTPLAN, label: "Payment Plan" },
  { value: PaymentSource.MEMBERSHIP, label: "Subscription" },
  { value: PaymentSource.VIRTUAL, label: "Virtual Payment" },
  { value: PaymentSource.TERMINAL, label: "Terminal Payment" },
  { value: PaymentSource.ONLINE, label: "Online Payment" },
  { value: PaymentSource.INSURANCE, label: "Insurance Payment" },
  { value: PaymentSource.PAYMENT_REQUEST, label: "Payment Request" },
  { value: PaymentSource.TREATMENT_PLAN, label: "Treatment Plan" },
];

type TransactionOptionType = {
  value?: PaymentType;
  label: string;
};

export const TransactionsOptions: Array<TransactionOptionType> = [
  { value: undefined, label: "All Transactions Source" },
  { value: PaymentType.PROMPTED, label: "Prompted" },
  { value: PaymentType.PROCESSED, label: "Processed" },
];

export const InvoiceStatusOptions: Array<{ value: string | undefined; label: string }> = [
  { value: undefined, label: "Filter by Status" },
  { value: "Submitted", label: "Submitted" },
  { value: "Drafted", label: "Drafted" },
  { value: "HasAmount", label: "Amount Greater Than $0.01" },
];

export const RecordOptions: Array<{ value: string | undefined; label: string }> = [
  { value: undefined, label: "Recorded Status" },
  { value: "recorded", label: "Recorded" },
  { value: "not_recorded", label: "Not Recorded" },
  { value: "not_applicable", label: "Not Applicable" },
];

export const InsuranceTypeOptions: Array<{ value: string | undefined; label: string }> = [
  { value: "claim", label: "Insurance Claims" },
  { value: "payments", label: "Insurance Payments" },
];

export const PercentageOptions: Array<{ value: number | undefined; label: string }> = [
  { value: 10, label: "10%" },
  { value: 20, label: "20%" },
];

export const ClaimStatusOptions: Array<{ value: ClaimStatus | undefined; label: string }> = [
  { value: undefined, label: "Filter By Claim Status" },
  { value: ClaimStatus.SENT, label: "Sent" },
  { value: ClaimStatus.RECEIVED, label: "Received" },
  { value: ClaimStatus.APPROVED, label: "Approved" },
  { value: ClaimStatus.PAID, label: "Paid" },
  { value: ClaimStatus.REJECTED, label: "Rejected" },
  { value: ClaimStatus.CANCELED, label: "Canceled" },
];

export const PaymentStatusOptions: Array<{ value: boolean | undefined; label: string }> = [
  { value: undefined, label: "Filter By Payment Status" },
  { value: true, label: "Recorded" },
  { value: false, label: "Non Recorded" },
];

export const ClaimAmountOptions: Array<{ value: string | undefined; label: string }> = [
  { value: "0_50", label: "$0.00 - $50.00" },
  { value: "50_500", label: "$50.01 - $500.00" },
  { value: "500_1500", label: "500.01 - $1,500" },
  { value: ">1500", label: "$1,500.01 - and up" },
];

export const BalanceDueAmountOptions: Array<{ value: string | undefined; label: string }> = [
  { value: "0_50", label: "$0.00 - $50.00" },
  { value: "50_500", label: "$50.01 - $500.00" },
  { value: "500_1500", label: "$500.01 - $1,500.00" },
  { value: "1500_5000", label: "$1,500.01 - $5,000.00" },
  { value: ">5000", label: "$5,000.01 - and up" },
];

export const OutstandingTypeOptions: Array<{ value: string | undefined; label: string }> = [
  { value: "outstanding", label: "Outstanding Accounts" },
  // { value: "insurance", label: "Insurance Claims" },
  // { value: "autocollection", label: "Auto Collection Accounts" },
];

export const StatementTypeOptions: Array<{ value: string | undefined; label: string }> = [
  { value: "accounts_due", label: "Ready to Generate" },
  { value: "statement", label: "Statement Table" },
];

export const OutstandingBalanceTypeOptions: Array<{ value: string | undefined; label: string }> = [
  { value: undefined, label: "All Balance Types" },
  { value: "balance", label: "Account Balance" },
  { value: "insurance", label: "Insurance Balance" },
];

export const AgingBucketOptions: Array<{ value: string | undefined; label: string }> = [
  { value: "0_30", label: "0 - 30" },
  { value: "30_60", label: "30 - 60" },
  { value: "60_90", label: "60 - 90" },
  { value: "90_120", label: "90 - 120" },
  { value: "120_150", label: "120 - 150" },
  { value: "150_180", label: "150 - 180" },
  { value: "180_360", label: "180 - 360" },
  { value: "360_720", label: "360 - 720" },
];

export const ProcessPaymentOptions: Array<MembershipOptionType> = [
  { value: MembershipType.YEARLY, label: "Yearly" },
  { value: MembershipType.MONTHLY, label: "Monthly" },
];

export const ProcessPaymentExtendedOptions: Array<{ value: PaymentCycle; label: string }> = [
  { value: PaymentCycle.WEEKLY, label: "Weekly" },
  { value: PaymentCycle.BIWEEKLY, label: "Biweekly" },
  { value: PaymentCycle.MONTHLY, label: "Monthly" },
  { value: PaymentCycle.YEARLY, label: "Yearly" },
];

export const PaymentPlanPeriodOptions: Array<{ value: PaymentCycle; label: string }> = [
  { value: PaymentCycle.WEEKLY, label: "Weekly" },
  { value: PaymentCycle.BIWEEKLY, label: "Biweekly" },
  { value: PaymentCycle.MONTHLY, label: "Monthly" },
  { value: PaymentCycle.YEARLY, label: "Yearly" },
];

export const PaymentCountOptions: Array<{ value: number; label: string }> = [
  { value: 2, label: "2 installments" },
  { value: 3, label: "3 installments" },
  { value: 4, label: "4 installments" },
  { value: 5, label: "5 installments" },
  { value: 6, label: "6 installments" },
  { value: 7, label: "7 installments" },
  { value: 8, label: "8 installments" },
  { value: 9, label: "9 installments" },
  { value: 10, label: "10 installments" },
  { value: 12, label: "12 installments" },
  { value: 15, label: "15 installments" },
  { value: 20, label: "20 installments" },
  { value: 30, label: "30 installments" },
];

export const EnrollmentFeeOptions: Array<{ value: number; label: string }> = [
  { value: 0, label: "No Fee" },
  { value: 1, label: "1%" },
  { value: 2, label: "2%" },
  { value: 3, label: "3%" },
  { value: 4, label: "4%" },
  { value: 5, label: "5%" },
  { value: 6, label: "6%" },
  { value: 7, label: "7%" },
  { value: 8, label: "8%" },
  { value: 9, label: "9%" },
  { value: 10, label: "10%" },
];

export const InsightOptions: Array<StringOptionsType> = [
  { value: "transactions", label: "Transactions Table" },
  { value: "outreach", label: "Messages Table" },
];

export type OfficeOptionsType = {
  value?: OfficeStatus;
  label: string;
};

export const OfficeFilterOptions: Array<OfficeOptionsType> = [
  { value: undefined, label: "Filter by Location Status" },
  { value: OfficeStatus.ACTIVE, label: "Active" },
  { value: OfficeStatus.INACTIVE, label: "Termed" },
  // { value: OfficeStatus.PAUSE, label: "Pause" },
  { value: OfficeStatus.ONBOARDED, label: "Onboarding" },
];

export const PatientsOptions = [
  { value: undefined, label: "Sort Directory By :" },
  { value: 1, label: "Only with balance" },
];

export const RowsPerPageOptions = [
  { value: 10, label: "10" },
  { value: 25, label: "25" },
  { value: 50, label: "50" },
  { value: 100, label: "100" },
];

export const DateRangeOptions = [
  { value: 0, label: "All Time" },
  { value: 1, label: "Month to Date" },
  { value: 2, label: "Today" },
  { value: 3, label: "Past 7 Days" },
  { value: 4, label: "Last Month" },
];

export const InsuranceClaimDateRangeOptions = [
  { value: 1, label: "Current Month" },
  { value: 2, label: "Trailing 3 Months" },
  { value: 3, label: "Trailing 6 Months" },
  { value: 4, label: "All Time" },
];

export const AppointmentStatusOptions = [
  { value: undefined, label: "All Status" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "CONFIRMED", label: "Confirmed" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

export const UpcomingAppointmentStatusOptions = [
  { value: undefined, label: "Confirmation Status" },
  { value: true, label: "Confirmed" },
  { value: false, label: "Unconfirmed" },
];

export const AppointmentFilterOptions = [
  { value: "appt_date", label: "Appointment Date" },
  { value: "schedule_date", label: "Schedule Date" },
];

export type ReportOptionType = {
  value: string;
  label: string;
  type: PracticeManagement;
  description: string;
};

export const ReportUploadOptions: Array<ReportOptionType> = [
  {
    value: "winoms_payment",
    label: "Payment",
    type: PracticeManagement.WINOMS,
    description: "Payment activity CSV exported from WinOMS.",
  },
  {
    value: "winoms_patient",
    label: "Patient(Daily)",
    type: PracticeManagement.WINOMS,
    description: "Daily patient demographics and account CSV from WinOMS.",
  },
  {
    value: "winoms_patient_sync",
    label: "Patient Sync",
    type: PracticeManagement.WINOMS,
    description: "Patient sync file from WinOMS with the columns this import expects.",
  },
  {
    value: "winoms_guarantor_mapping",
    label: "Guarantor Mapping",
    type: PracticeManagement.WINOMS,
    description: "Guarantor-to-patient relationship mapping from WinOMS.",
  },
  {
    value: "winoms_special_patient",
    label: "Patient(Daily)",
    type: PracticeManagement.WINOMS_SPECIAL,
    description:
      "Use this option for daily sync of patients from WinOMS Special-enabled offices. You can use balance&location filter",
  },
  {
    value: "winoms_special_patient_sync",
    label: "Patient(Full)",
    type: PracticeManagement.WINOMS_SPECIAL,
    description: "Use this option if you want to sync patients from WinOMS Special-enabled offices.",
  },
  {
    value: "winoms_appointment",
    label: "Appointment",
    type: PracticeManagement.WINOMS,
    description: "Appointment schedule export from WinOMS.",
  },
  {
    value: "winoms_ins_claim",
    label: "Insurance Claim",
    type: PracticeManagement.WINOMS,
    description: "Insurance claim detail export from WinOMS.",
  },
  {
    value: "winoms_ins_claim",
    label: "Insurance Claim",
    type: PracticeManagement.WINOMS_SPECIAL,
    description: "Insurance claim detail export (WinOMS Special schema).",
  },
  {
    value: "winoms_cell_phone",
    label: "Cell Phone",
    type: PracticeManagement.WINOMS,
    description: "Patient mobile numbers export from WinOMS.",
  },
  {
    value: "winoms_cell_phone",
    label: "Cell Phone",
    type: PracticeManagement.WINOMS_SPECIAL,
    description: "Patient mobile numbers export (WinOMS Special schema).",
  },
  {
    value: "winoms_billing_code",
    label: "Billing Code",
    type: PracticeManagement.WINOMS_SPECIAL,
    description:
      "Pay/Adj code export from WinOMS. If patient exists in this report, then auto collection will be enabled for the patient.",
  },
  {
    value: "winoms_billing_code",
    label: "Billing Code",
    type: PracticeManagement.WINOMS,
    description:
      "Pay/Adj code export from WinOMS. If patient exists in this report, then auto collection will be enabled for the patient.",
  },
  {
    value: "dentrix",
    label: "Aging Receivable",
    type: PracticeManagement.DENTRIX,
    description: "Aging receivable report CSV from Dentrix.",
  },
  {
    value: "dentrix_patient",
    label: "Patient",
    type: PracticeManagement.DENTRIX_NI,
    description: "Patient demographics export from Dentrix (NI workflow).",
  },
  {
    value: "dentrix_ascend_transaction",
    label: "Transaction Detail",
    type: PracticeManagement.DENTRIX_ASCEND_NI,
    description: "Transaction-level ledger detail from Dentrix Ascend.",
  },
  {
    value: "dentrix_ascend",
    label: "Aged Receivable",
    type: PracticeManagement.DENTRIX_ASCEND,
    description: "Aged receivable balances from Dentrix Ascend.",
  },
  {
    value: "dentrix_ascend_ni",
    label: "Aged Receivable",
    type: PracticeManagement.DENTRIX_ASCEND_NI,
    description: "Aged receivable export from Dentrix Ascend.",
  },
  {
    value: "dentrix_ascend_ni_service",
    label: "Service Date",
    type: PracticeManagement.DENTRIX_ASCEND_NI,
    description: "Aged receivable or balance data grouped by service date (Ascend NI).",
  },
  {
    value: "dentrix_ascend_ni_patient",
    label: "Patient",
    type: PracticeManagement.DENTRIX_ASCEND_NI,
    description: "Patient export from Dentrix Ascend.",
  },
  {
    value: "dentrix_ascend_claim",
    label: "Insurance Claim",
    type: PracticeManagement.DENTRIX_ASCEND,
    description: "Insurance claim export from Dentrix Ascend.",
  },
  {
    value: "dentrix",
    label: "Aged Receivable",
    type: PracticeManagement.DENTRIX_NI,
    description: "Aged receivable report from Dentrix.",
  },
  {
    value: "eaglesoft_patient",
    label: "Patient",
    type: PracticeManagement.EAGLESOFT_NI,
    description: "Patient export from Eaglesoft (NI).",
  },
  {
    value: "eaglesoft_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.EAGLESOFT_NI,
    description: "Aged receivable report from Eaglesoft.",
  },
  {
    value: "eaglesoft_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.EAGLESOFT,
    description: "Aged receivable report from Eaglesoft.",
  },
  {
    value: "eaglesoft_guarantor",
    label: "Enhance Guarantor",
    type: PracticeManagement.EAGLESOFT,
    description: "Guarantor enhancement data from Eaglesoft.",
  },
  {
    value: "curve_appointment",
    label: "Appointment",
    type: PracticeManagement.CURVE,
    description: "Appointment export from Curve Hero.",
  },
  {
    value: "curve_payment",
    label: "Payment",
    type: PracticeManagement.CURVE,
    description: "Payment activity export from Curve Hero.",
  },
  {
    value: "curve-balance",
    label: "Aged Receivable",
    type: PracticeManagement.CURVE,
    description: "Aged receivable / balance export from Curve Hero.",
  },
  {
    value: "curve-light-balance",
    label: "Light Patient Demo",
    type: PracticeManagement.CURVE,
    description: "Lightweight patient demographics for Curve balance workflows.",
  },
  {
    value: "curve-insurance-claim",
    label: "Insurance Claim",
    type: PracticeManagement.CURVE,
    description: "Insurance claim export from Curve Hero.",
  },
  {
    value: "fuse_patient_seen",
    label: "Patient Seen",
    type: PracticeManagement.FUSE,
    description: "Patients seen report from Fuse.",
  },
  {
    value: "fuse_patient_last_seen",
    label: "Patient Last Service",
    type: PracticeManagement.FUSE,
    description: "Last service date per patient from Fuse.",
  },
  {
    value: "fuse_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.FUSE,
    description: "Aged receivable export from Fuse.",
  },
  {
    value: "opendental_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.OPENDENTAL,
    description: "Aged receivable report from Open Dental.",
  },
  {
    value: "opendental_guarantor",
    label: "Enhance Guarantor",
    type: PracticeManagement.OPENDENTAL,
    description: "Guarantor enhancement data from Open Dental.",
  },
  {
    value: "opendental_patient_seen",
    label: "Patient Seen",
    type: PracticeManagement.OPENDENTAL_NI,
    description: "Patients seen export from Open Dental (NI).",
  },
  {
    value: "opendental_prompted_payment",
    label: "Prompted Payment",
    type: PracticeManagement.OPENDENTAL_NI,
    description: "Prompted payment activity from Open Dental (NI).",
  },
  {
    value: "opendental_prompted_payment",
    label: "Prompted Payment",
    type: PracticeManagement.OPENDENTAL,
    description: "Prompted payment activity from Open Dental.",
  },
  {
    value: "opendental_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.OPENDENTAL_NI,
    description: "Aged receivable report from Open Dental (NI).",
  },
  {
    value: "opendental_special_aged_receivable",
    label: "Guarantor Aged Receivable",
    type: PracticeManagement.OPENDENTAL,
    description: "Guarantor-level aged receivable export from Open Dental.",
  },
  {
    value: "denticon_patient",
    label: "Patient",
    type: PracticeManagement.DENTICON,
    description: "Patient export from Denticon.",
  },
  {
    value: "denticon_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.DENTICON,
    description: "Aged receivable report from Denticon.",
  },
  {
    value: "softdent_patient",
    label: "Patient",
    type: PracticeManagement.SOFTDENT,
    description: "Patient export from SoftDent.",
  },
  {
    value: "softdent_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.SOFTDENT,
    description: "Aged receivable report from SoftDent.",
  },
  {
    value: "sensicloud_patient",
    label: "Patient",
    type: PracticeManagement.SENSI_CLOUD,
    description: "Patient export from Sensi Cloud.",
  },
  {
    value: "dns_patient",
    label: "Patient",
    type: PracticeManagement.DNS,
    description: "Patient demographics export from DNS.",
  },
  {
    value: "dns_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.DNS,
    description: "Aged receivable report from DNS.",
  },
  {
    value: "dns_patient_address",
    label: "Patient Address",
    type: PracticeManagement.DNS,
    description: "Patient mailing and address fields from DNS.",
  },
  {
    value: "dns_patient_address",
    label: "Patient Address",
    type: PracticeManagement.DSN_CLOUD,
    description: "Patient address export from DSN Cloud.",
  },
  {
    value: "dns_patient",
    label: "Patient",
    type: PracticeManagement.DSN,
    description: "Patient demographics export from DSN.",
  },
  {
    value: "dns_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.DSN,
    description: "Aged receivable report from DSN.",
  },
  {
    value: "dns_patient_address",
    label: "Patient Address",
    type: PracticeManagement.DSN,
    description: "Patient mailing and address fields from DSN.",
  },
  {
    value: "dsn_cloud_daily_journal",
    label: "Daily Journal",
    type: PracticeManagement.DSN,
    description: "Daily journal entries from DSN (on-prem).",
  },
  {
    value: "dsn_cloud_daily_journal",
    label: "Daily Journal",
    type: PracticeManagement.DNS,
    description: "Daily journal entries from DNS.",
  },
  {
    value: "dns_unpaid_insurance_claim",
    label: "Unpaid Insurance Claim",
    type: PracticeManagement.DNS,
    description: "Outstanding insurance claims export from DNS.",
  },
  {
    value: "dns_unpaid_insurance_claim",
    label: "Unpaid Insurance Claim",
    type: PracticeManagement.DSN,
    description: "Outstanding insurance claims export from DSN.",
  },
  {
    value: "dns_unpaid_insurance_claim",
    label: "Unpaid Insurance Claim",
    type: PracticeManagement.DSN_CLOUD,
    description: "Outstanding insurance claims export from DSN Cloud.",
  },
  {
    value: "dsn_account_report",
    label: "Account Category",
    type: PracticeManagement.DNS,
    description: "Sync account category",
  },
  {
    value: "dsn_account_report",
    label: "Account Category",
    type: PracticeManagement.DSN,
    description: "Sync account category",
  },
  {
    value: "dsn_account_report",
    label: "Account Category",
    type: PracticeManagement.DSN_CLOUD,
    description: "Sync account category",
  },
  {
    value: "dentimax_patient",
    label: "Patient",
    type: PracticeManagement.DENTIMAX,
    description: "Patient export from Dentimax.",
  },
  {
    value: "dentimax_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.DENTIMAX,
    description: "Aged receivable report from Dentimax.",
  },
  // { value: "cloud9_aged_receivable", label: "Aged Receivable", type: PracticeManagement.CLOUD9 },
  {
    value: "cloud9_new_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.CLOUD9,
    description: "Aged receivable export from Cloud 9.",
  },
  {
    value: "oryx_patient_contact",
    label: "Patient Contact",
    type: PracticeManagement.ORYX,
    description: "Patient contact information from Oryx.",
  },
  {
    value: "oryx_appointment",
    label: "Appointment",
    type: PracticeManagement.ORYX,
    description: "Appointment export from Oryx.",
  },
  {
    value: "oryx_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.ORYX,
    description: "Aged receivable report from Oryx.",
  },
  {
    value: "dsn_cloud_patient",
    label: "Patient",
    type: PracticeManagement.DSN_CLOUD,
    description: "Patient demographics from DSN Cloud.",
  },
  {
    value: "dsn_cloud_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.DSN_CLOUD,
    description: "Aged receivable report from DSN Cloud.",
  },
  {
    value: "dsn_cloud_daily_journal",
    label: "Daily Journal",
    type: PracticeManagement.DSN_CLOUD,
    description: "Daily journal export from DSN Cloud.",
  },
  {
    value: "oms_vision_patient",
    label: "Patient",
    type: PracticeManagement.OMS_VISION,
    description: "Patient export from OMS Vision.",
  },
  {
    value: "oms_vision_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.OMS_VISION,
    description: "Aged receivable report from OMS Vision.",
  },
  {
    value: "practiceworks_patient",
    label: "Patient",
    type: PracticeManagement.PRACTICEWORKS,
    description: "Patient export from PracticeWorks.",
  },
  {
    value: "practiceworks_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.PRACTICEWORKS,
    description: "Aged receivable report from PracticeWorks.",
  },
  // { value: "pbs_endo_patient", label: "Patient", type: PracticeManagement.PBS_ENDO },
  {
    value: "pbs_endo_appointment",
    label: "Patient",
    type: PracticeManagement.PBS_ENDO,
    description: "Patient-related export from PBS Endo (appointment listing schema).",
  },
  {
    value: "pbs_endo_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.PBS_ENDO,
    description: "Aged receivable report from PBS Endo.",
  },
  {
    value: "pbs_endo_old_appointment",
    label: "Old Appointment",
    type: PracticeManagement.PBS_ENDO,
    description: "Only for old appointment report from PBS Endo.",
  },
  {
    value: "pbs_scheduler",
    label: "Appointment",
    type: PracticeManagement.PBS_ENDO,
    description: "Scheduler appointment export from PBS Endo.",
  },
  {
    value: "mac_practice_patient",
    label: "Patient",
    type: PracticeManagement.MAC_PRACTICE,
    description: "Patient demographics from MacPractice.",
  },
  {
    value: "mac_practice_patient_email",
    label: "Patient Email",
    type: PracticeManagement.MAC_PRACTICE,
    description: "Patient email addresses from MacPractice.",
  },
  {
    value: "mac_practice_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.MAC_PRACTICE,
    description: "Aged receivable report from MacPractice.",
  },
  {
    value: "carestack_patient",
    label: "Patient",
    type: PracticeManagement.CARESTACK,
    description: "Patient export from CareStack.",
  },
  {
    value: "carestack_ar",
    label: "Aged Receivable",
    type: PracticeManagement.CARESTACK,
    description: "Accounts receivable / aging from CareStack.",
  },
  {
    value: "dolphin_patient",
    label: "Patient",
    type: PracticeManagement.DOLPHIN,
    description: "Patient export from Dolphin.",
  },
  {
    value: "dolphin_ar",
    label: "Aged Receivable",
    type: PracticeManagement.DOLPHIN,
    description: "Aged receivable report from Dolphin.",
  },
  {
    value: "peak_patient",
    label: "Patient",
    type: PracticeManagement.PEAK,
    description: "Patient list export from Peak.",
  },
  {
    value: "peak_aged_receivable",
    label: "Aged Receivable",
    type: PracticeManagement.PEAK,
    description: "Aged receivable report from Peak.",
  },
  {
    value: "special_account_report",
    label: "Account Report",
    type: PracticeManagement.SPECIAL,
    description:
      "Member/account roster with Last Name, First Name, Address, City, State, Postal, phones, emails, and Year of Birth. Creates or updates Billing Patient records.",
  },
  ...Object.values(PracticeManagement).map((practice) => ({
    value: "payrix_token",
    label: "Import Payrix token",
    type: practice,
    description:
      "CSV of vaulted Payrix cards (source_token_id, payrix_token, payrix_token_id, card_number, card_type, token_expiration, customer_first_name, customer_last_name). Matches patients by first and last name and stores source_token_id as the payment method.",
  })),
];

type GroupReportOptionType = {
  value: string;
  label: string;
  groupId: number;
};
export const GroupReportOptions: Array<GroupReportOptionType> = [
  { value: "smilebrand-claims", label: "Insurance Claims", groupId: 3 },
  { value: "beacon-claims", label: "Insurance Claims", groupId: 4 },
];

export const PearlyUploadOptions = [
  { value: "pearly_processed", label: "PAYMENT PROCESSED" },
  { value: "pearly_prompted", label: "PAYMENT PROMPTED" },
  { value: "pearly_arstatus", label: "AR STATUS" },
];

export const timeZoneList = [
  { value: "America/New_York", label: "Eastern Time" },
  { value: "America/Chicago", label: "Central Time" },
  { value: "America/Denver", label: "Mountain Time" },
  { value: "America/Phoenix", label: "Mountain Time (no DST)" },
  { value: "America/Los_Angeles", label: "Pacific Time" },
  { value: "America/Anchorage", label: "Alaska Time" },
  { value: "America/Adak", label: "Hawaii-Aleutian" },
  { value: "Pacific/Honolulu", label: "Hawaii-Aleutian Time (no DST)" },
];

export const practiceList = Object.entries(PracticeManagement).map(([key, value]) => ({
  value: value,
  label: formatPMS(key as PracticeManagement),
}));

export const SubscriptionPlanStatusOptions = [
  { value: true, label: "Active" },
  { value: false, label: "Inactive" },
  // { value: "draft", label: "Draft" },
];

export const PaymentOptions = [
  { value: "terminal", label: "Terminal Payment" },
  { value: "virtual", label: "Virtual Payment" },
  { value: "insurance", label: "Insurance Payment" },
  { value: "request", label: "Payment Request" },
  { value: "unreferenced-refund", label: "Unreferenced Refund" },
  { value: "batch-request", label: "Batch Payment Request" },
  // { value: "link", label: "Payment Link" },
  // { value: "paymentplan", label: "Enroll in Payment Plan" },
  // { value: "subscription", label: "Enroll in Subscription" },
];

export const CommunicationOptions = [
  { value: "0-100", label: "0 - 100" },
  { value: "101-500", label: "101 - 500" },
  { value: "501-5000", label: "501 - 5,000" },
  { value: "50001", label: "5,0001 - Up" },
];

export const OutstandingOptions = [
  { value: "0-50000", label: "0 - $50,000" },
  { value: "50001-100000", label: "$50,001 - $100,000" },
  { value: "100001-500000", label: "$100,001 - $500,000" },
  { value: "500001", label: "$500,001 - Up" },
];

export const GrossVolumeOptions = [
  { value: "0-10000", label: "0 - $10,000" },
  { value: "10001-50000", label: "$10,001 - $50,000" },
  { value: "50001-100000", label: "$50,001 - $100,000" },
  { value: "10001-250000", label: "$100,001 - $250,000" },
  { value: "250001-500000", label: "$250,001 - $500,000" },
  { value: "500001", label: "$500,001 - Up" },
];

export const MissingPaymentSourceOptions = [
  { value: undefined, label: "Integration" },
  { value: PaymentSource.AUTOMATEDAR, label: "Auto Collection" },
  { value: PaymentSource.PAYMENTPLAN, label: "Payment Plan" },
  { value: PaymentSource.MEMBERSHIP, label: "Subscription" },
  { value: PaymentSource.VIRTUAL, label: "Virtual Payment" },
  { value: PaymentSource.TERMINAL, label: "Terminal Payment" },
  { value: PaymentSource.ONLINE, label: "Online Payment" },
  { value: PaymentSource.INSURANCE, label: "Insurance Payment" },
  { value: PaymentSource.TREATMENT_PLAN, label: "Treatment Plan" },
];

export const AdjustmentTypeOptions = [
  { value: AccountableType.PATIENT, label: "Payment Adjustment" },
  { value: AccountableType.INSURANCE_PLAN, label: "Insurance Adjustment" },
];

export const AdjustmentDirectionOptions = [
  { value: 1, label: "Credit" },
  { value: -1, label: "Charge" },
];

export const PayoutStatusOptions = [
  { value: undefined, label: "All Status" },
  { value: "APPROVED", label: "Approved" },
  { value: "AWAITING_APPROVAL", label: "Awaiting" },
];

export const PayoutAmountOptions = [
  { value: undefined, label: "Filter by Payout Amount" },
  { value: "APPROVED", label: "Approved" },
  { value: "AWAITING_APPROVAL", label: "Awaiting" },
];

export type PRTemplateOption = {
  value: "basic" | "reminder" | "follow-up" | "urgent" | "final" | "encourage" | "template-1" | "custom";
  label: string;
};

export const PRTemplateOptions: PRTemplateOption[] = [
  { value: "basic", label: "Basic Payment Request" },
  { value: "reminder", label: "Friendly Reminder" },
  { value: "follow-up", label: "Professional Follow-Up" },
  { value: "urgent", label: "Urgent Payment Request" },
  { value: "final", label: "Final Notice" },
  { value: "encourage", label: "Friendly Encouragement" },
  { value: "template-1", label: "Template 1" },
  { value: "custom", label: "Custom" },
];

export const StatementOptions = [
  { value: StatementTemplate.DEFAULT, label: "Default Template" },
  { value: StatementTemplate.REMINDER, label: "Friendly Reminder" },
  { value: StatementTemplate.FOLLOWUP, label: "Professional Follow-Up" },
  { value: StatementTemplate.URGENT, label: "Urgent Payment Request" },
  { value: StatementTemplate.FINAL, label: "Final Notice" },
  { value: StatementTemplate.ENCOURAGEMENT, label: "Friendly Encouragement" },
  { value: StatementTemplate.CUSTOM, label: "Custom Template" },
];

export const ClaimSubStatusOptions = [
  {
    value: ClaimSubStatus.CLAIM_NOF_ISR_INCORRECT,
    label: "Claim not on file (NOF) - insurance demographics missing/incorrect",
  },
  {
    value: ClaimSubStatus.CLAIM_NOF_PAT_INCORRECT,
    label: "Claim not on file (NOF) - patient demographics missing/incorrect",
  },
  { value: ClaimSubStatus.CLEARINGHOUSE_ERROR, label: "Clearinghouse error" },
  { value: ClaimSubStatus.CODING_ISSUE, label: "Coding issue" },
  { value: ClaimSubStatus.CREDENTIALING_ISSUE, label: "Credentialing issue" },
  { value: ClaimSubStatus.ISR_TERMINATED, label: "Insurance terminated" },
  { value: ClaimSubStatus.MISSING_DOC, label: "Missing documentation" },
];

export const StatementTemplateOptions = [
  { value: undefined, label: "Filter by Template" },
  { value: StatementTemplate.DEFAULT, label: "Default Template" },
];

export const StatementSourceOptions = [
  { value: undefined, label: "Filter by Source" },
  { value: StatementSource.MANUAL, label: "Manual" },
];

export const StatementStatusOptions = [
  { value: undefined, label: "Filter by Status" },
  { value: StatementStatus.GENERATED, label: "Generated" },
  { value: StatementStatus.MAILED, label: "Mailed" },
];

export const IntakeFormStatusOptions = [
  { value: undefined, label: "Filter by Status" },
  { value: IntakeFormStatus.INCOMPLETE, label: "Incomplete" },
  { value: IntakeFormStatus.COMPLETED, label: "Completed" },
  { value: IntakeFormStatus.INPROGRESS, label: "In Progress" },
];

export const messageTemplateNames = {
  MEMBERSHIP_COMPLETE_REQUEST: "Membership Complete Request",
  MEMBERSHIP_ACTIVE_NOTIFICATION: "Membership Active Notification",
  MEMBERSHIP_RENEWAL: "Membership Renewal",
  PAYMENT_SUCCESS: "Payment Success",
  OUTREACH: "Outreach",
  OUTREACH_FIRST: "Outreach - First",
  OUTREACH_REMINDER: "Outreach - Reminder",
  OUTREACH_LAST: "Outreach - Last",
  FORM_REQUEST: "Form Request",
  FORM_COMPLETE: "Form Complete",
  APPOINTMENT_REMINDER_TWO_WEEKS: "Appointment Reminder - 2 Weeks",
  APPOINTMENT_REMINDER_ONE_WEEK: "Appointment Reminder - 1 Week",
  APPOINTMENT_REMINDER_FIVE_DAYS: "Appointment Reminder - 5 Days",
  APPOINTMENT_REMINDER_FOUR_DAYS: "Appointment Reminder - 4 Days",
  APPOINTMENT_REMINDER_THREE_DAYS: "Appointment Reminder - 3 Days",
  APPOINTMENT_REMINDER_TWO_DAYS: "Appointment Reminder - 2 Days",
  APPOINTMENT_REMINDER_ONE_DAY: "Appointment Reminder - 1 Day",
  APPOINTMENT_REMINDER_TODAY: "Appointment Reminder - Today",
  PAYMENT_REQUEST_BASIC: "Payment Request - Basic",
  PAYMENT_REQUEST_REMINDER: "Payment Request - Reminder",
  PAYMENT_REQUEST_FOLLOWUP: "Payment Request - Follow-Up",
  PAYMENT_REQUEST_URGENT: "Payment Request - Urgent",
  PAYMENT_REQUEST_FINAL: "Payment Request - Final",
  PAYMENT_REQUEST_ENCOURAGEMENT: "Payment Request - Encouragement",
  PAYMENT_REQUEST_TEMPLATE_1: "Payment Request - Template 1",
  PAYMENT_REQUEST_CUSTOM: "Payment Request - Custom",
} as const;

export type MessageTemplateKey = keyof typeof messageTemplateNames;
export type MessageTemplateName = (typeof messageTemplateNames)[MessageTemplateKey];

/** Auto Collection outreach sequence — matches `handleArReminder` phase selection. */
export const outreachAutoCollectionMessageOptions: {
  value: (typeof messageTemplateNames)["OUTREACH_FIRST" | "OUTREACH_REMINDER" | "OUTREACH_LAST"];
  label: string;
}[] = [
  { value: messageTemplateNames.OUTREACH_FIRST, label: "First message" },
  { value: messageTemplateNames.OUTREACH_REMINDER, label: "Reminders" },
  { value: messageTemplateNames.OUTREACH_LAST, label: "Last message" },
];

export const SurveyTheme = {
  themeName: "default",
  colorPalette: "light",
  isPanelless: true,
  cssVariables: {
    "--sjs-general-backcolor": "rgba(255, 255, 255, 1)",
    "--sjs-general-backcolor-dark": "rgba(248, 248, 248, 1)",
    "--sjs-general-backcolor-dim": "rgba(255, 255, 255, 1)",
    "--sjs-general-backcolor-dim-light": "rgba(249, 249, 249, 1)",
    "--sjs-general-backcolor-dim-dark": "rgba(243, 243, 243, 1)",
    "--sjs-general-forecolor": "rgba(0, 0, 0, 0.91)",
    "--sjs-general-forecolor-light": "rgba(0, 0, 0, 0.45)",
    "--sjs-general-dim-forecolor": "rgba(0, 0, 0, 0.91)",
    "--sjs-general-dim-forecolor-light": "rgba(0, 0, 0, 0.45)",
    "--sjs-primary-backcolor": "rgba(11, 11, 11, 1)",
    "--sjs-primary-backcolor-light": "rgba(25, 179, 148, 0.1)",
    "--sjs-primary-backcolor-dark": "rgba(20, 164, 139, 1)",
    "--sjs-primary-forecolor": "rgba(255, 255, 255, 1)",
    "--sjs-primary-forecolor-light": "rgba(255, 255, 255, 0.25)",
    "--sjs-base-unit": "8px",
    "--sjs-corner-radius": "4px",
    "--sjs-secondary-backcolor": "rgba(255, 152, 20, 1)",
    "--sjs-secondary-backcolor-light": "rgba(255, 152, 20, 0.1)",
    "--sjs-secondary-backcolor-semi-light": "rgba(255, 152, 20, 0.25)",
    "--sjs-secondary-forecolor": "rgba(255, 255, 255, 1)",
    "--sjs-secondary-forecolor-light": "rgba(255, 255, 255, 0.25)",
    "--sjs-shadow-small": "0px 1px 2px 0px rgba(0, 0, 0, 0.15)",
    "--sjs-shadow-small-reset": "0px 0px 0px 0px rgba(0, 0, 0, 0.15)",
    "--sjs-shadow-medium": "0px 2px 6px 0px rgba(0, 0, 0, 0.1)",
    "--sjs-shadow-large": "0px 8px 16px 0px rgba(0, 0, 0, 0.1)",
    "--sjs-shadow-inner": "inset 0px 1px 2px 0px rgba(0, 0, 0, 0.15)",
    "--sjs-shadow-inner-reset": "inset 0px 0px 0px 0px rgba(0, 0, 0, 0.15)",
    "--sjs-border-light": "rgba(0, 0, 0, 0.09)",
    "--sjs-border-default": "rgba(0, 0, 0, 0.16)",
    "--sjs-border-inside": "rgba(0, 0, 0, 0.16)",
    "--sjs-special-red": "rgba(229, 10, 62, 1)",
    "--sjs-special-red-light": "rgba(229, 10, 62, 0.1)",
    "--sjs-special-red-forecolor": "rgba(255, 255, 255, 1)",
    "--sjs-special-green": "rgba(25, 179, 148, 1)",
    "--sjs-special-green-light": "rgba(25, 179, 148, 0.1)",
    "--sjs-special-green-forecolor": "rgba(255, 255, 255, 1)",
    "--sjs-special-blue": "rgba(67, 127, 217, 1)",
    "--sjs-special-blue-light": "rgba(67, 127, 217, 0.1)",
    "--sjs-special-blue-forecolor": "rgba(255, 255, 255, 1)",
    "--sjs-special-yellow": "rgba(255, 152, 20, 1)",
    "--sjs-special-yellow-light": "rgba(255, 152, 20, 0.1)",
    "--sjs-special-yellow-forecolor": "rgba(255, 255, 255, 1)",
    "--sjs-article-font-xx-large-textDecoration": "none",
    "--sjs-article-font-xx-large-fontWeight": "700",
    "--sjs-article-font-xx-large-fontStyle": "normal",
    "--sjs-article-font-xx-large-fontStretch": "normal",
    "--sjs-article-font-xx-large-letterSpacing": "0",
    "--sjs-article-font-xx-large-lineHeight": "64px",
    "--sjs-article-font-xx-large-paragraphIndent": "0px",
    "--sjs-article-font-xx-large-textCase": "none",
    "--sjs-article-font-x-large-textDecoration": "none",
    "--sjs-article-font-x-large-fontWeight": "700",
    "--sjs-article-font-x-large-fontStyle": "normal",
    "--sjs-article-font-x-large-fontStretch": "normal",
    "--sjs-article-font-x-large-letterSpacing": "0",
    "--sjs-article-font-x-large-lineHeight": "56px",
    "--sjs-article-font-x-large-paragraphIndent": "0px",
    "--sjs-article-font-x-large-textCase": "none",
    "--sjs-article-font-large-textDecoration": "none",
    "--sjs-article-font-large-fontWeight": "700",
    "--sjs-article-font-large-fontStyle": "normal",
    "--sjs-article-font-large-fontStretch": "normal",
    "--sjs-article-font-large-letterSpacing": "0",
    "--sjs-article-font-large-lineHeight": "40px",
    "--sjs-article-font-large-paragraphIndent": "0px",
    "--sjs-article-font-large-textCase": "none",
    "--sjs-article-font-medium-textDecoration": "none",
    "--sjs-article-font-medium-fontWeight": "700",
    "--sjs-article-font-medium-fontStyle": "normal",
    "--sjs-article-font-medium-fontStretch": "normal",
    "--sjs-article-font-medium-letterSpacing": "0",
    "--sjs-article-font-medium-lineHeight": "32px",
    "--sjs-article-font-medium-paragraphIndent": "0px",
    "--sjs-article-font-medium-textCase": "none",
    "--sjs-article-font-default-textDecoration": "none",
    "--sjs-article-font-default-fontWeight": "400",
    "--sjs-article-font-default-fontStyle": "normal",
    "--sjs-article-font-default-fontStretch": "normal",
    "--sjs-article-font-default-letterSpacing": "0",
    "--sjs-article-font-default-lineHeight": "28px",
    "--sjs-article-font-default-paragraphIndent": "0px",
    "--sjs-article-font-default-textCase": "none",
  },
};
